import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ProviderPriceListProductUnlockModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/providerPriceListProductUnlock/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/providerPriceListProductUnlock/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/providerPriceListProductUnlock/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/providerPriceListProductUnlock/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/providerPriceListProductUnlock/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/providerPriceListProductUnlock/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/providerPriceListProductUnlock/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/providerPriceListProductUnlock/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/providerPriceListProductUnlock/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/providerPriceListProductUnlock/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/providerPriceListProductUnlock/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/providerPriceListProductUnlock/DELETE_FAILURE'

export type ProviderPriceListProductUnlockModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: ProviderPriceListProductUnlockModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProviderPriceListProductUnlocks(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().providerPriceListProductUnlocks.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/provider_price_list_product_unlocks.json', options)
      .then(({ data }: { data: ProviderPriceListProductUnlockModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_PRODUCT_UNLOCK_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadProviderPriceListProductUnlock(providerPriceListProductUnlockId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.providerPriceListProductUnlocks.loadedForKeys]
    const loadedIds = [...state.providerPriceListProductUnlocks.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (providerPriceListProductUnlockId && !loadedIds.includes(providerPriceListProductUnlockId)){
      loadedIds.push(providerPriceListProductUnlockId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/provider_price_list_product_unlocks/${providerPriceListProductUnlockId}.json`, options)
      .then(({ data }: { data: ProviderPriceListProductUnlockModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_PRODUCT_UNLOCK)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createProviderPriceListProductUnlock(providerPriceListProductUnlock: Partial<ProviderPriceListProductUnlockModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider_price_list_product_unlock: providerPriceListProductUnlock,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/provider_price_list_product_unlocks.json', options, config)
      .then(({ data }: { data: ProviderPriceListProductUnlockModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_PRODUCT_UNLOCK)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateProviderPriceListProductUnlock(
  providerPriceListProductUnlock: UpdateParams<ProviderPriceListProductUnlockModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider_price_list_product_unlock: providerPriceListProductUnlock,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_list_product_unlocks/${providerPriceListProductUnlock.id}.json`, options, config)
      .then(({ data }: { data: ProviderPriceListProductUnlockModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_PRODUCT_UNLOCK)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteProviderPriceListProductUnlock(providerPriceListProductUnlock: DeleteParams<ProviderPriceListProductUnlockModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/provider_price_list_product_unlocks/${providerPriceListProductUnlock.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_PRODUCT_UNLOCK)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ProviderPriceListProductUnlockModuleState = initialState,
  action: Action = { type: '' },
): ProviderPriceListProductUnlockModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
